import React, { useState, useMemo, useEffect } from 'react';
import { aiAPI, taskBatchAPI } from '../../../api/services';

interface FeatureBatchEstimationProps {
  featureId: string;
  featureName: string;
  projectId: string;
  tasks: any[];
  featureApproved: boolean;
  onRefresh: () => void;
}

const FeatureBatchEstimation: React.FC<FeatureBatchEstimationProps> = ({
  featureId, featureName, projectId, tasks, featureApproved, onRefresh,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<{
    featurePoints: number;
    tasks: { taskId: string; points: number; reasoning: string }[];
  } | null>(null);
  const [aiMsg, setAiMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const aiTaskMap = useMemo(() => {
    const m = new Map<string, { points: number; reasoning: string }>();
    if (aiResult?.tasks) {
      aiResult.tasks.forEach(t => m.set(t.taskId, { points: t.points, reasoning: t.reasoning }));
    }
    return m;
  }, [aiResult]);

  // Tasks needing SP and not in any batch
  const needsEstimation = useMemo(() =>
    tasks.filter((t: any) =>
      !t.batchId && t.status !== 'done' && t.needsBatchApproval &&
      (!t.storyPoints || t.storyPoints <= 0) && !aiTaskMap.has(t._id)
    ), [tasks, aiTaskMap]);

  // Tasks with SP and not in any batch
  const readyToSubmit = useMemo(() =>
    tasks.filter((t: any) =>
      !t.batchId && t.status !== 'done' && t.needsBatchApproval &&
      ((t.storyPoints && t.storyPoints > 0) || aiTaskMap.has(t._id))
    ), [tasks, aiTaskMap]);

  const totalActionable = needsEstimation.length + readyToSubmit.length;

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSubmitMsg(null);
    }
  }, [open]);

  if (totalActionable === 0) return null;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleGroup = (groupTasks: any[]) => {
    const ids = groupTasks.map((t: any) => t._id);
    const allSelected = ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const handleAiEstimate = async () => {
    setAiLoading(true);
    setAiMsg('');
    setAiResult(null);
    try {
      const res = await aiAPI.estimateStoryPoints(featureId, true);
      const d = res.data;
      if (d.fallback) {
        setAiMsg(d.message || 'AI estimation not available — assign points manually');
      } else if (d.data) {
        setAiResult(d.data);
        setAiMsg(`Estimated ${d.data.featurePoints} SP total across ${d.data.tasks?.length || 0} tasks`);
        onRefresh();
      }
    } catch {
      setAiMsg('Estimation failed — assign points manually');
    }
    setAiLoading(false);
  };

  const handleBatchSubmit = async () => {
    const submitableIds = Array.from(selectedIds).filter(id =>
      readyToSubmit.some((t: any) => t._id === id)
    );
    if (submitableIds.length === 0) return;
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const items = submitableIds.map(id => ({ itemId: id, itemType: 'task' as const }));
      const draftRes = await taskBatchAPI.createDraft({
        projectId,
        featureId,
        name: `${featureName} — New Tasks`,
        items,
      });
      const batchId = draftRes.data?.data?._id;
      if (batchId) await taskBatchAPI.confirmSubmit(batchId);
      setSelectedIds(new Set());
      setSubmitMsg({ type: 'success', text: `${items.length} task${items.length > 1 ? 's' : ''} submitted for batch approval` });
      onRefresh();
    } catch (err: any) {
      setSubmitMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to submit batch' });
    }
    setSubmitting(false);
    setTimeout(() => setSubmitMsg(null), 4000);
  };

  const selectedReadyCount = Array.from(selectedIds).filter(id =>
    readyToSubmit.some((t: any) => t._id === id)
  ).length;

  const selectedNeedsCount = Array.from(selectedIds).filter(id =>
    needsEstimation.some((t: any) => t._id === id)
  ).length;

  const renderSelectableRow = (t: any) => {
    const ai = aiTaskMap.get(t._id);
    return (
      <div key={t._id}
        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${selectedIds.has(t._id) ? 'border-blue-300 bg-blue-50/60' : 'border-gray-100 bg-white hover:bg-gray-50/80'}`}
        onClick={() => toggleSelect(t._id)}>
        <input type="checkbox" checked={selectedIds.has(t._id)} readOnly
          className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer shrink-0" />
        <span className="text-sm text-gray-800 truncate flex-1">{t.title}</span>
        {ai ? (
          <span className="text-xs text-blue-600 font-medium shrink-0 tabular-nums" title={ai.reasoning}>{ai.points} SP</span>
        ) : t.storyPoints > 0 ? (
          <span className="text-xs text-blue-600 font-medium shrink-0 tabular-nums">{t.storyPoints} SP</span>
        ) : (
          <span className="text-xs text-red-500 shrink-0">No SP</span>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Inline trigger */}
      <button
        onClick={() => setOpen(true)}
        className="mt-2 flex items-center gap-1.5 text-xs text-gray-400 hover:text-blue-600 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        {`${totalActionable} task${totalActionable !== 1 ? 's' : ''} need SP review`}
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}
          onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden flex flex-col animate-[modalFadeIn_0.2s_ease-out]"
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Batch SP Review</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{featureName}</p>
                </div>
                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

              {/* Banners */}
              {aiMsg && !aiLoading && (
                <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${aiResult ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                  {aiResult ? (
                    <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                  )}
                  <span className="flex-1">{aiMsg}</span>
                  <button onClick={() => setAiMsg('')} className="shrink-0 text-current opacity-60 hover:opacity-100">&times;</button>
                </div>
              )}
              {submitMsg && (
                <div className={`text-xs px-3 py-2 rounded-lg ${submitMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {submitMsg.text}
                </div>
              )}

              {/* ── Group 1: Needs estimation ── */}
              {needsEstimation.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer" onClick={e => { e.preventDefault(); toggleGroup(needsEstimation); }}>
                        <input type="checkbox" readOnly
                          checked={needsEstimation.length > 0 && needsEstimation.every((t: any) => selectedIds.has(t._id))}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer shrink-0" />
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Needs estimation</span>
                      </label>
                      <span className="text-[10px] text-gray-300 tabular-nums">{needsEstimation.length}</span>
                    </div>
                    {selectedNeedsCount > 0 && (
                      <button onClick={handleAiEstimate} disabled={aiLoading}
                        className="flex items-center gap-1.5 text-[10px] border border-blue-200 text-blue-600 px-2.5 py-1 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors font-medium">
                        {aiLoading ? (
                          <><div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />Estimating...</>
                        ) : (
                          <><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>Estimate SP</>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {needsEstimation.map(renderSelectableRow)}
                  </div>
                </div>
              )}

              {/* ── Group 2: Ready to submit ── */}
              {readyToSubmit.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="flex items-center gap-1.5 cursor-pointer" onClick={e => { e.preventDefault(); toggleGroup(readyToSubmit); }}>
                      <input type="checkbox" readOnly
                        checked={readyToSubmit.length > 0 && readyToSubmit.every((t: any) => selectedIds.has(t._id))}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-400 cursor-pointer shrink-0" />
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Ready to submit</span>
                    </label>
                    <span className="text-[10px] text-gray-300 tabular-nums">{readyToSubmit.length}</span>
                  </div>
                  <div className="space-y-1">
                    {readyToSubmit.map(renderSelectableRow)}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {totalActionable === 0 && (
                <div className="text-center py-6">
                  <p className="text-xs text-gray-400">No tasks need SP review</p>
                </div>
              )}
            </div>

            {/* Footer — submit when ready tasks are selected */}
            {selectedReadyCount > 0 && (
              <div className="px-5 py-3 border-t border-gray-100 shrink-0 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {selectedReadyCount} task{selectedReadyCount !== 1 ? 's' : ''} selected
                </span>
                <button onClick={handleBatchSubmit} disabled={submitting}
                  className="text-xs bg-gray-900 text-white px-4 py-1.5 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors flex items-center gap-1.5">
                  {submitting ? (
                    <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting...</>
                  ) : (
                    'Submit for Batch Approval'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeatureBatchEstimation;
